// src/components/Register.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
  Link,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import { useNavigate, Link as RouterDomLink, Link as RouterLink } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import SEOHead from '../SEOHead';
import AuthSplitShell from '../auth/AuthSplitShell';
import AuthSeparator from '../auth/AuthSeparator';
import OAuthProviderGrid from '../auth/OAuthProviderGrid';
import { authTitleSx, authSubtitleSx, authLegalTextSx } from '../auth/authSplitShellSx';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { Input, Button } from '../ui';
import { acme } from '../../theme/acmeTheme';

function passwordToggleInputProps(showPassword, onToggle) {
  return {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={onToggle} edge="end" size="small">
          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
  };
}

const Register = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();
  const [loginError, setLoginError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const validatePassword = (pwd) => {
    if (pwd.length < 12) {
      setPasswordError('Password must be at least 12 characters long.');
      return false;
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&].+$/;
    if (!passwordRegex.test(pwd)) {
      setPasswordError(
        'Password must contain uppercase, lowercase, number, and special character (@$!%*?&).',
      );
      return false;
    }
    setPasswordError('');
    return true;
  };

  const checkLoginUnique = async () => {
    if (!login) return;
    try {
      const res = await axios.get(`/api/auth/check-duplicate-login?login=${encodeURIComponent(login)}`);
      setLoginError(res.data.exists ? 'A user with this login already exists.' : '');
    } catch (error) {
      console.error('Error checking login uniqueness:', error);
    }
  };

  const checkEmailUnique = async () => {
    if (!email) return;
    try {
      const res = await axios.get(`/api/auth/check-duplicate-email?email=${encodeURIComponent(email)}`);
      setEmailError(res.data.exists ? 'A user with this email already exists.' : '');
    } catch (error) {
      console.error('Error checking email uniqueness:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }
    if (!validatePassword(password)) return;
    if (!agreedToPrivacyPolicy) {
      setErrorMessage('You must agree to the Privacy Policy to register');
      return;
    }
    if (!agreedToTerms) {
      setErrorMessage('You must agree to the Terms of Service to register');
      return;
    }
    if (!executeRecaptcha) {
      setErrorMessage('reCAPTCHA is not ready yet. Please wait a moment and try again.');
      return;
    }

    setLoading(true);
    let captchaToken;
    try {
      captchaToken = await executeRecaptcha('register');
    } catch (e) {
      setErrorMessage('Failed to verify reCAPTCHA. Please refresh the page and try again.');
      setLoading(false);
      return;
    }

    try {
      await axios.post('/api/auth/register', { login, email, password, captcha: captchaToken });
      setRegisteredEmail(email);
      setSuccessDialogOpen(true);
      setSuccessMessage('Registration successful! Please check your email to verify your account.');
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        setErrorMessage(
          error.response?.data?.error ||
            'Too many registration attempts. Please wait about an hour and try again.',
        );
      } else {
        setErrorMessage(error.response?.data?.error || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead pageName="register" />
      <Dialog open={successDialogOpen} onClose={() => navigate('/login')} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>Registration successful</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: acme.colors.brand, mb: 2 }} />
          <Typography sx={{ mb: 2 }}>
            A verification email has been sent to{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {registeredEmail}
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
            Please check your inbox and click the verification link to activate your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={() => navigate('/login')}>Go to sign in</Button>
        </DialogActions>
      </Dialog>

      <AuthSplitShell>
        <Box>
          <Typography component="h1" sx={authTitleSx}>
            Create your account
          </Typography>
          <Typography sx={authSubtitleSx}>
            Join Mailsball and start sending email & SMS campaigns.
          </Typography>
        </Box>

        <OAuthProviderGrid
          onGoogle={() => { window.location.href = '/api/auth/google'; }}
          onFacebook={() => { window.location.href = '/api/auth/facebook'; }}
          onLinkedIn={() => { window.location.href = '/api/auth/linkedin'; }}
        />

        <AuthSeparator />

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 1.5 }}>
          <Typography sx={{ fontSize: '0.75rem', color: acme.colors.mutedForegroundSolid }}>
            Enter your details to create a Mailsball account
          </Typography>
          <Input
            label="Login"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            onBlur={checkLoginUnique}
            error={Boolean(loginError)}
            helperText={loginError}
            required
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={checkEmailUnique}
            error={Boolean(emailError)}
            helperText={emailError}
            required
          />
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (e.target.value) validatePassword(e.target.value);
              else setPasswordError('');
            }}
            error={Boolean(passwordError)}
            helperText={passwordError}
            required
            InputProps={passwordToggleInputProps(showPassword, () => setShowPassword((p) => !p))}
            autoComplete="new-password"
          />
          <Input
            label="Confirm password"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            InputProps={passwordToggleInputProps(showPassword, () => setShowPassword((p) => !p))}
            autoComplete="new-password"
          />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Checkbox
              id="terms-of-service-checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              sx={{ color: acme.colors.brand, '&.Mui-checked': { color: acme.colors.brand }, p: 0.5, mt: 0.25 }}
            />
            <Typography variant="body2" component="label" htmlFor="terms-of-service-checkbox" sx={{ color: acme.colors.mutedForegroundSolid }}>
              I agree to the{' '}
              <Link component={RouterLink} to="/terms-of-service" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </Link>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Checkbox
              id="privacy-policy-checkbox"
              checked={agreedToPrivacyPolicy}
              onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
              sx={{ color: acme.colors.brand, '&.Mui-checked': { color: acme.colors.brand }, p: 0.5, mt: 0.25 }}
            />
            <Typography variant="body2" component="label" htmlFor="privacy-policy-checkbox" sx={{ color: acme.colors.mutedForegroundSolid }}>
              I agree to the{' '}
              <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </Link>
            </Typography>
          </Box>

          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Creating account…' : 'Continue with email'}
          </Button>
        </Box>

        <Button component={RouterDomLink} to="/login" variant="outline" fullWidth size="lg">
          Already have an account? Sign in
        </Button>

        <Typography sx={authLegalTextSx}>
          By clicking continue, you agree to our{' '}
          <Link component={RouterLink} to="/terms-of-service">Terms of Service</Link> and{' '}
          <Link component={RouterLink} to="/privacy-policy">Privacy Policy</Link>.
        </Typography>
      </AuthSplitShell>

      <PageSnackbars {...snackbarProps} />
    </>
  );
};

export default Register;
